import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface ImageCarouselProps {
  images: string[];
  title?: string;
  height?: number;
}

function ImageCarousel({ images, title = 'Image', height = 400 }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);

  if (!images || images.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography color="text.secondary">No images available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Main Image Display */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <img
          src={images[currentIndex]}
          alt={`${title} ${currentIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
          onError={(e) => {
            console.error('Failed to load image:', images[currentIndex]);
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 8,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </Box>

      {/* Image Counter */}
      {images.length > 1 && (
        <Box display="flex" justifyContent="center" mt={1}>
          <Typography variant="body2" color="text.secondary">
            {currentIndex + 1} of {images.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ImageCarousel;
